import Autocomplete from "@mui/material/Autocomplete";
import type { SxProps, Theme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import { exhaustiveCheck } from "core/utils/exhaustiveCheck";
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
  type RegisterOptions,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

/**
 * Props for the ReactHookFreeSolo component.
 */
export interface ReactHookFreeSoloProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name: TName;
  label: string;
  rules?: Omit<
    RegisterOptions<TFieldValues, TName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  required?: boolean;
  sx?: SxProps<Theme>;
  disabled?: boolean;
  fullWidth?: boolean;
  control?: Control<TFieldValues>;
  options: string[];
  autoFocus?: boolean;
}

/**
 * The ReactHookFreeSolo component.
 * This component is a custom form field component that provides an autocomplete input with free text entry.
 */
export function ReactHookFreeSolo<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  label,
  control,
  disabled,
  fullWidth,
  required,
  rules,
  options,
  autoFocus,
}: ReactHookFreeSoloProps<TFieldValues, TName>) {
  const { t } = useTranslation("Fixhub");
  const labelPrefix = required ? "* " : "";
  const actualLabel = `${labelPrefix}${label}`;
  return (
    <Controller
      name={name}
      rules={{
        required: required
          ? t("Next:Core:FieldRequiredValidationMessage")
          : false,
        ...rules,
      }}
      control={control}
      render={({
        field: { ref: inputRef, ...field },
        fieldState: { error },
      }) => (
        <Autocomplete
          includeInputInList
          autoHighlight
          disabled={disabled}
          options={options}
          freeSolo
          autoFocus={autoFocus}
          color={error ? "error" : undefined}
          fullWidth={fullWidth}
          {...field}
          onChange={(_, value, reason) => {
            switch (reason) {
              case "selectOption":
                field.onChange(value);
                break;
              case "removeOption":
                break;
              case "clear":
                field.onChange("");
                break;
              case "createOption":
                field.onChange(value);
                break;
              case "blur":
                break;
              default:
                exhaustiveCheck(reason);
            }
          }}
          onInputChange={(_, newInputValue) => {
            field.onChange(newInputValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={actualLabel}
              error={Boolean(error)}
              inputRef={inputRef}
              helperText={Boolean(error) && error?.message}
            />
          )}
        />
      )}
    />
  );
}
